import React from 'react';

import dayjs from 'dayjs';
import { Calendar, CircleDashed, Fingerprint, History, Layers, SlidersHorizontal, Target, Type } from 'lucide-react';
import capitalize from 'lodash/capitalize';
import toNumber from 'lodash/toNumber';

import {
  NexoyaContentFilter,
  NexoyaContentFilterFieldName,
  NexoyaContentFilterOperator,
  NexoyaFieldAllowedValues,
  NexoyaFieldOperation,
} from '../../types';
import { GLOBAL_DATE_FORMAT } from '../../utils/dates';

import { DataTableFilterOption, FilterType, Operator } from './types';

const DEFAULT_DAYJS_YEAR_WHEN_NO_YEAR_IS_PRESENT_IN_DATE = 2001;

export const humanizeFieldName = (fieldName: string) => {
  const fieldMapping: Record<string, string> = {
    providerId: 'Provider id',
    collectionId: 'Collection id',
    teamId: 'Team ID',
    title: 'Title',
    collectionType: 'Collection Type',
    parentCollectionId: 'Parent Collection id',
    parentTitle: 'Parent Title',
    startDate: 'Start Date',
    endDate: 'End Date',
    latestMeasurementDataDate: 'Last Measurement date',
    earliestMeasurementDataDaysAgo: 'Earliest data',
    bidStrategy: 'Bid Strategy',
  };

  return fieldMapping[fieldName] || fieldName.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
};

export const OPERATORS_MAP: Record<string, Operator> = {
  eq: { label: 'Is', value: NexoyaContentFilterOperator.Eq, humanReadable: 'Is' },
  ne: { label: 'Is not', value: NexoyaContentFilterOperator.Ne, humanReadable: 'Not' },
  gte: { label: 'Is on or after', value: NexoyaContentFilterOperator.Gte, humanReadable: 'On or after' },
  lte: { label: 'Is on or before', value: NexoyaContentFilterOperator.Lte, humanReadable: 'On or before' },
  contains: { label: 'Contains', value: NexoyaContentFilterOperator.Contains, humanReadable: 'Contains' },
  notContains: { label: 'Does not contain', value: NexoyaContentFilterOperator.NotContains, humanReadable: 'Not' },
  // startsWith: { label: 'Starts with', value: 'startsWith', humanReadable: 'Starts with' },
  // endsWith: { label: 'Ends with', value: 'endsWith', humanReadable: 'Ends with' },
  // isNull: { label: 'Is empty', value: 'isNull', humanReadable: 'Empty' },
  // isNotNull: { label: 'Is not empty', value: 'isNotNull', humanReadable: 'Not empty' },
};

export const DAYS_AGO_UNIT = 'days ago';

// Operators whose generic wording does not fit the field, e.g. `gte` on a number of days ago.
const FIELD_OPERATORS_MAP: Partial<Record<string, Record<string, Operator>>> = {
  [NexoyaContentFilterFieldName.EarliestMeasurementDataDaysAgo]: {
    gte: {
      label: `… or more ${DAYS_AGO_UNIT}`,
      value: NexoyaContentFilterOperator.Gte,
      humanReadable: '',
      valueSuffix: `+ ${DAYS_AGO_UNIT}`,
    },
  },
};

export const getOperatorForField = (fieldName: string, operator: string): Operator | undefined =>
  FIELD_OPERATORS_MAP[fieldName]?.[operator] ?? OPERATORS_MAP[operator];

// Utility to map incoming operations to UI operators
export const mapOperationsToUI = (operators: string[], fieldName?: string) =>
  operators.map((op) => getOperatorForField(fieldName, op)).filter(Boolean); // Filter out undefined mappings

export const getIconForField = (fieldName: NexoyaContentFilterFieldName) => {
  switch (fieldName) {
    case NexoyaContentFilterFieldName.ContentId:
      return <Fingerprint className="h-3.5 w-3.5" />;
    case NexoyaContentFilterFieldName.Title:
      return <Type className="h-3.5 w-3.5" />;
    case NexoyaContentFilterFieldName.ContentType:
      return <Layers className="h-3.5 w-3.5" />;
    case NexoyaContentFilterFieldName.ParentContentId:
      return <Layers className="h-3.5 w-3.5" />;
    case NexoyaContentFilterFieldName.ParentTitle:
      return <Type className="h-3.5 w-3.5" />;
    case NexoyaContentFilterFieldName.StartDate:
      return <Calendar className="h-3.5 w-3.5" />;
    case NexoyaContentFilterFieldName.EndDate:
      return <Calendar className="h-3.5 w-3.5" />;
    case NexoyaContentFilterFieldName.LatestMeasurementDataDate:
      return <Calendar className="h-3.5 w-3.5" />;
    case NexoyaContentFilterFieldName.EarliestMeasurementDataDaysAgo:
      return <History className="h-3.5 w-3.5" />;
    case NexoyaContentFilterFieldName.BidStrategy:
      return <Target className="h-3.5 w-3.5" />;
    case NexoyaContentFilterFieldName.Status:
      return <CircleDashed className="h-3.5 w-3.5" />;
    case NexoyaContentFilterFieldName.BudgetType:
      return <Layers className="h-3.5 w-3.5" />;
    default:
      return <SlidersHorizontal className="h-3.5 w-3.5" />;
  }
};

export const getTypeForField = (allowedFields: NexoyaFieldAllowedValues) => {
  if (allowedFields.enumOptionsNumber?.length) {
    return 'numberArr';
  } else if (allowedFields.enumOptionsString?.length) {
    return 'stringArr';
  }

  return allowedFields.fieldType;
};

export const getFilterValueInputBasedOnType = (type: FilterType, filterValues: string[]) => {
  const value = {
    date: null,
    number: null,
    numberArr: null,
    string: null,
    stringArr: null,
    boolean: null,
  };

  switch (type) {
    case 'date':
      const parsedDate = dayjs(filterValues?.[0]);

      // Check if the parsed date doesn't have a year and set it to the current year
      if (!parsedDate.isValid() || parsedDate.year() === DEFAULT_DAYJS_YEAR_WHEN_NO_YEAR_IS_PRESENT_IN_DATE) {
        value.date = parsedDate.year(dayjs().year()).format(GLOBAL_DATE_FORMAT);
      } else {
        value.date = parsedDate.format(GLOBAL_DATE_FORMAT);
      }
      break;
    case 'string':
      value.string = filterValues?.[0];
      break;
    case 'boolean':
      value.boolean = filterValues?.[0] === 'true';
      break;
    case 'number':
      value.number = toNumber(filterValues?.[0]);
      break;
    case 'numberArr':
      value.numberArr = filterValues?.map((val) => toNumber(val));
      break;
    case 'stringArr':
      value.stringArr = filterValues;
      break;
    default:
      break;
  }
  return value;
};

export const getFilterValuesFromFilter = (filter: NexoyaContentFilter) => {
  if (filter.value?.stringArr) return filter.value.stringArr;
  if (filter.value?.string) return [filter.value.string];
  if (filter.value?.numberArr) return filter.value.numberArr;
  if (filter.value?.number != null) return [String(filter.value.number)];
  if (filter.value?.date) return [dayjs(filter.value.date).format('DD MMM, YYYY')];
  return [];
};

export const displayFilterValue = (filter: NexoyaContentFilter) => {
  const filterValues = getFilterValuesFromFilter(filter);

  if (filterValues.length > 1) {
    return `${filterValues.length} selected`;
  } else if (filter?.value?.stringArr && filterValues?.length > 1) {
    // @ts-expect-error
    return filterValues.map(capitalize);
  }

  return filterValues[0];
};

export const withValueSuffix = (value: string, operator?: Operator) => `${value}${operator?.valueSuffix ?? ''}`;

export type FilterChipDisplayText = { hasValue: boolean; valueText: string };

/**
 * Single source of truth for the filter chip's right-hand-side text.
 * Always prefixes the operator's humanReadable form so faceted filters
 * (Bid Strategy, Status, etc.) and free-text filters (Title, etc.) render
 * consistently — e.g. "Not Bid Cap" rather than just "Bid Cap" for `ne`.
 */
export function getFilterChipDisplayText(args: {
  selectedOption: Pick<DataTableFilterOption, 'options'>;
  selectedValues: Set<string>;
  freeTextValue: string;
  selectedOperator?: Operator;
  /**
   * How many values it takes before the list collapses to "N selected". Three by default,
   * which is what every caller got when it was hardcoded. The labels sheet raises it, so
   * its drawer chip and the chip on the category card behind it collapse the same rule at
   * the same count.
   */
  collapseThreshold?: number;
}): FilterChipDisplayText {
  const { selectedOption, selectedValues, freeTextValue, selectedOperator, collapseThreshold = 3 } = args;
  const prefix = selectedOperator?.humanReadable ?? '';
  const compose = (suffix: string) => (prefix ? `${prefix} ${suffix}` : suffix);
  const withUnit = (value: string) => withValueSuffix(value, selectedOperator);

  if (selectedValues.size >= collapseThreshold) {
    return { hasValue: true, valueText: compose(`${selectedValues.size} selected`) };
  }

  if (selectedValues.size > 0) {
    const values = Array.from(selectedValues);

    if (selectedOption.options.length > 0) {
      const labels = values.map((val) => {
        const option = selectedOption.options.find((item) => item.value === val);
        return option?.label ?? val;
      });
      return { hasValue: true, valueText: compose(labels.join(', ')) };
    }

    return { hasValue: true, valueText: compose(values.map(withUnit).join(' OR ')) };
  }

  if (freeTextValue) {
    return { hasValue: true, valueText: compose(withUnit(freeTextValue)) };
  }

  return { hasValue: false, valueText: '' };
}

export type FilterChipParts = {
  /** The operator's `humanReadable` lead-in, `''` when the clause carries no operator. */
  operator: string;
  /** Every value the chip prints, already resolved from wire value to option label. */
  values: string[];
  /** What joins two printed values: `', '` for an options list, `' OR '` for free text. */
  separator: string;
  /** `'N selected'` once the list reaches the collapse threshold, `null` below it. */
  collapsed: string | null;
};

/**
 * The same right-hand side as `getFilterChipDisplayText`, as parts, so the chip can name
 * its button from exactly what it paints. A SIBLING, not a refactor: the flat string is
 * the shipped contract, and `chipParts.test.ts` pins the two together case for case.
 * `collapsed` sits alongside the full `values` list; the caller decides which to print.
 */
export function getFilterChipParts(args: {
  selectedOption: Pick<DataTableFilterOption, 'options'>;
  selectedValues: Set<string>;
  freeTextValue: string;
  selectedOperator?: Operator;
  /** How many values it takes before the list collapses to "N selected". Three by default. */
  collapseThreshold?: number;
}): FilterChipParts {
  const { selectedOption, selectedValues, freeTextValue, selectedOperator, collapseThreshold = 3 } = args;
  const operator = selectedOperator?.humanReadable ?? '';
  const hasOptions = selectedOption.options.length > 0;
  const separator = hasOptions ? ', ' : ' OR ';
  const base = { operator, separator };

  if (selectedValues.size > 0) {
    const values = Array.from(selectedValues).map((val) =>
      hasOptions
        ? (selectedOption.options.find((item) => item.value === val)?.label ?? val)
        : withValueSuffix(val, selectedOperator),
    );

    return {
      ...base,
      values,
      collapsed: selectedValues.size >= collapseThreshold ? `${selectedValues.size} selected` : null,
    };
  }

  if (freeTextValue) {
    return { ...base, values: [withValueSuffix(freeTextValue, selectedOperator)], collapsed: null };
  }

  return { ...base, values: [], collapsed: null };
}

/**
 * The parts back as the one string `getFilterChipDisplayText` would have produced — the
 * chip's accessible name, so the spoken chip and the painted chip cannot disagree.
 *
 * It does NOT decide whether the clause has a value: a clause holding a single blank
 * keyword joins to `''` but still counts as valued upstream, so that call stays with the
 * caller, which reads it off `collapsed`/`values` just like the renderer does.
 */
export function joinFilterChipValueText(parts: FilterChipParts): string {
  const value = parts.collapsed ?? parts.values.join(parts.separator);
  return parts.operator ? `${parts.operator} ${value}` : value;
}

export const excludeProviderAndParentCollectionFields = (filter: NexoyaContentFilter) =>
  filter.fieldName !== NexoyaContentFilterFieldName.SourceProviderId &&
  filter.fieldName !== NexoyaContentFilterFieldName.ParentContentId;

export function normalizeFilter(filter: any) {
  // Remove __typename at the top level
  const { __typename, ...rest } = filter;
  // Normalize the value: remove __typename and remove keys with null
  const normalizedValue = Object.keys(rest.value).reduce(
    (acc, key) => {
      if (key === '__typename') return acc;
      // Only include keys that are not null
      if (rest.value[key] !== null) {
        acc[key] = rest.value[key];
      }
      return acc;
    },
    {} as Record<string, any>,
  );

  return {
    ...rest,
    value: normalizedValue,
  };
}

export function camelCaseToWords(s: string) {
  const result = s.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
}

export const sortFields = (fields: NexoyaFieldOperation[]) => {
  const FIELD_PRIORITY = {
    [NexoyaContentFilterFieldName.ContentType]: 1,
    [NexoyaContentFilterFieldName.Title]: 2,
    [NexoyaContentFilterFieldName.Status]: 3,
    [NexoyaContentFilterFieldName.StartDate]: 4,
    [NexoyaContentFilterFieldName.EndDate]: 5,
    [NexoyaContentFilterFieldName.BidStrategy]: 6,
    [NexoyaContentFilterFieldName.LatestMeasurementDataDate]: 7,
    [NexoyaContentFilterFieldName.ParentTitle]: 8,
  };

  return [...fields].sort((a, b) => {
    const priorityA = FIELD_PRIORITY[a.fieldName] || Number.MAX_SAFE_INTEGER;
    const priorityB = FIELD_PRIORITY[b.fieldName] || Number.MAX_SAFE_INTEGER;
    return priorityA - priorityB;
  });
};

export const cleanSearchDimensions = (params: any[] = []) => {
  const filtered = params
    ?.filter((p) => p.dimensionName && p.values)
    .map(({ dimensionName, values }) => ({ dimensionName, values }));
  return filtered?.length ? filtered : [];
};
